import { checkExtensionInstalled, getExtensionStoreUrl, isSupportedDesktopBrowser } from "@/lib/extensionChecker";
import { getUserInfo as getCachedUserInfo } from "@/services/Cookies/localStorage";
import { useAuth } from "@/services/Firebase/Authcontext";
import { IWarningResponse, ISystemAlert } from "@/types/WarningTypes";
import { Alert, AlertTitle, Stack, Button } from "@mui/material";
import React, { useState, useEffect } from "react";

interface WarningDisplayProps {
	warnings: IWarningResponse[];
	systemAlerts?: ISystemAlert[];
}

export default function WarningDisplay({ warnings, systemAlerts = [] }: WarningDisplayProps) {
	const { sessionUser: user } = useAuth();
	const [hidden, setHidden] = useState<any>({});
	const [extensionInstalled, setExtensionInstalled] = useState<boolean | null>(null);
	const [isCompatibleBrowser, setIsCompatibleBrowser] = useState<boolean>(true);
	const [isMobile, setIsMobile] = useState<boolean>(false);
	const [userType, setUserType] = useState<string | null>(null);

	// Check extension installation on mount
	useEffect(() => {
		const checkExtension = async () => {
			const installed = await checkExtensionInstalled();
			// console.log("Extension installed:", installed);
			setExtensionInstalled(installed);
		};

		const checkBrowser = () => {
			const userAgent = navigator.userAgent;
			const mobile = /Mobile|Android|iPhone|iPad/.test(userAgent);
			setIsMobile(mobile);
			const compatible = isSupportedDesktopBrowser();
			setIsCompatibleBrowser(compatible);
		};

		const checkUserType = () => {
			if (!user?.uid) return;
			const userInfo = getCachedUserInfo(user.uid);

			if (!userInfo || userInfo.professionType === 1) {
				setUserType("student");
			} else {
				setUserType("professional");
			}
		};

		// Check browser compatibility
		checkBrowser();

		// Only check extension if we have the extension ID
		if (process.env.NEXT_PUBLIC_EXTENSION_ID) {
			checkExtension();
		}

		if (user?.uid) {
			checkUserType();
		}
	}, [user?.uid]);

	// Create extension alert if not installed or incompatible browser (desktop only)
	const extensionAlert: ISystemAlert | null =
		userType === "professional" && !isMobile
			? !isCompatibleBrowser
				? {
					id: "browser-not-compatible",
					title: "Use a Supported Browser",
					description: "Use Chrome, Firefox, or Edge for better job tracking. The Jobstacky browser extension works seamlessly on all three browsers.",
					severity: "info",
					dismissable: true,
				}
				: extensionInstalled === false
				? {
					id: "extension-not-installed",
					title: "Jobstacky Extension Not Detected",
					description: "Use the Jobstacky browser extension for easy job tracking and better control over your professional opportunities.",
					severity: "info",
					buttonText: "Install Extension",
					buttonHref: getExtensionStoreUrl(),
					dismissable: true,
				}
				: null
			: null;

	// Combine all alerts (extension alert first, then system alerts, then database warnings)
	const allAlerts: (ISystemAlert | IWarningResponse)[] = [...(extensionAlert ? [extensionAlert] : []), ...systemAlerts, ...warnings];

	const isSystemAlert = (alert: ISystemAlert | IWarningResponse): alert is ISystemAlert => {
		return "buttonAction" in alert || "buttonHref" in alert;
	};

	const getAlertId = (alert: ISystemAlert | IWarningResponse): string => {
		return isSystemAlert(alert) ? alert.id : alert.id.toString();
	};

	const isAllHidden = () => {
		return allAlerts.every((alert) => hidden[getAlertId(alert)]);
	};

	if (!allAlerts || allAlerts.length === 0) return null;
	if (isAllHidden()) return null;

	return (
		<Stack direction="column" spacing={1} p={2} pb={0}>
			{allAlerts.map((alert, index) => {
				const alertId = getAlertId(alert);

				if (hidden[alertId]) return null;

				return (
					<Alert
						key={index}
						severity={alert.severity as any}
						{...(isSystemAlert(alert)
							? {
								action: alert.buttonText ? (
									alert.buttonAction ? (
										<Button onClick={alert.buttonAction}>{alert.buttonText}</Button>
									) : alert.buttonHref ? (
										<Button href={alert.buttonHref}>{alert.buttonText}</Button>
									) : null
								) : undefined,
							}
							: {
								action: alert.buttonLink ? (
									<a href={alert.buttonLink} target="_blank">
										<Button>{alert.buttonText || "View"}</Button>
									</a>
								) : undefined,
							})}
						{...((!isSystemAlert(alert) || alert.dismissable !== false) && {
							onClose: () => setHidden({ ...hidden, [alertId]: true }),
						})}
					>
						{alert.title && <AlertTitle>{alert.title}</AlertTitle>}
						{alert.description}
					</Alert>
				);
			})}
		</Stack>
	);
}
